import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Alert, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HotelReservation } from '@bandido/shared/types';
import { hotelReservationTransport } from './hotelReservationTransport';

/** A preview never changes the booking. Confirmation always names its durable job. */
export function HotelReconciliationPanel({
  reservation,
  disabled,
  onUpdated,
  showOutcome = true,
}: {
  reservation: HotelReservation;
  disabled: boolean;
  showOutcome?: boolean;
  onUpdated: (value: HotelReservation) => void;
}) {
  const { t } = useTranslation();
  const key = 'hotelReservation.reconciliation';
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [started, setStarted] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const refresh = reservation.refresh;
  const pending = refresh && ['queued', 'claimed', 'retryable_failed'].includes(refresh.status);
  const candidate = refresh?.candidate;
  useEffect(() => {
    setAccepted(false);
  }, [refresh?.jobId]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useQuery(['hotelReconciliation', reservation.id], () => hotelReservationTransport.read(reservation.id), {
    enabled: Boolean(pending) && now - started < 120000,
    refetchInterval: 3000,
    retry: false,
    onSuccess: onUpdated,
  });
  const preview = useMutation(
    () => hotelReservationTransport.previewReconciliation(reservation.id, reference.trim(), reason.trim()),
    {
      retry: false,
      onSuccess: (value) => {
        setAccepted(false);
        setStarted(Date.now());
        onUpdated(value);
      },
    },
  );
  const confirm = useMutation(() => hotelReservationTransport.confirmReconciliation(reservation.id, refresh!.jobId, Boolean(candidate?.missingFields?.length)), {
    retry: false,
    onSuccess: onUpdated,
  });
  const check = useMutation(() => hotelReservationTransport.read(reservation.id), {
    retry: false,
    onSuccess: (value) => {
      setStarted(Date.now());
      onUpdated(value);
    },
  });
  const busy = disabled || preview.isLoading || confirm.isLoading || check.isLoading || Boolean(pending);
  const valid = candidate && Date.parse(candidate.expiresAt) > now;
  return (
    <Stack spacing={2} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle1">{t(`${key}Title`)}</Typography>
      <Alert severity="info">{t(`${key}Help`)}</Alert>
      <TextField
        label={t(`${key}Reference`)}
        value={reference}
        disabled={busy}
        inputProps={{ maxLength: 200 }}
        onChange={(event) => setReference(event.target.value)}
      />
      <TextField
        label={t(`${key}Reason`)}
        value={reason}
        disabled={busy}
        inputProps={{ maxLength: 500 }}
        onChange={(event) => setReason(event.target.value)}
      />
      <Button disabled={busy || !reference.trim() || reason.trim().length < 5} onClick={() => preview.mutate()}>
        {t(`${key}Preview`)}
      </Button>
      {pending && <Alert severity="info">{t(`${key}Pending`)}</Alert>}
      {pending && now - started >= 120000 && (
        <Button disabled={disabled || check.isLoading} onClick={() => check.mutate()}>
          {t(`${key}Check`)}
        </Button>
      )}
      {(preview.isError || confirm.isError || check.isError) && <Alert severity="warning">{t(`${key}Error`)}</Alert>}
      {showOutcome && refresh?.outcome && refresh.outcome !== 'candidate_ready' && refresh.manualReviewRequired && (
        <Alert severity="warning">{t(`${key}Outcome_${refresh.outcome}`, { defaultValue: t(`${key}Outcome_inconclusive_result`) })}</Alert>
      )}
      {candidate && (
        <>
          <Typography>
            {reservation.hotelSnapshot.propertyName} · {candidate.checkIn} — {candidate.checkOut}
          </Typography>
          <Typography>
            {candidate.holder ?? t(`${key}HolderMissing`)} · {candidate.supplierReference}
          </Typography>
          <Typography>
            {t(`${key}Price`)}: {candidate.price.currency_code}{' '}
            {(candidate.price.amount_minor / 10 ** candidate.price.currency_exponent).toFixed(
              candidate.price.currency_exponent,
            )}
          </Typography>
          {Boolean(candidate.missingFields?.length) && <Alert severity="warning">
            {t(`${key}MissingDetails`)} {candidate.missingFields!.map((field) => t(`${key}Missing_${field}`)).join(', ')}
          </Alert>}
          {!valid && <Alert severity="warning">{t(`${key}Expired`)}</Alert>}
          <FormControlLabel
            control={
              <Checkbox checked={accepted} disabled={busy || !valid} onChange={(_, checked) => setAccepted(checked)} />
            }
            label={t(candidate.missingFields?.length ? `${key}AcceptMissing` : `${key}Accept`)}
          />
          <Button variant="contained" disabled={busy || !valid || !accepted} onClick={() => confirm.mutate()}>
            {t(`${key}Confirm`)}
          </Button>
        </>
      )}
    </Stack>
  );
}
